import React, { useState, useEffect } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { translate } from '../../../services/translate';
import Map from '../../../components/Map';
import MyAppText from '../../../components/MyAppText';
import { dispatchService } from '../../../services/central-api/dispatch';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useIsFocused } from '@react-navigation/native';
import MapFilter from './MapFilter';
import { WindowInformation } from '../../../services/window-information';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import Header from '../../../components/Header';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';


export default function Resume() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();

    const [agentPositions, setAgentPositions] = useState<UnitMapPosition[]>([]);
    const [occurrencePositions, setOccurrencePositions] = useState<OccurrencePosition[]>([]);
    const [policeStationsPositions, setPoliceStationsPositions] = useState<PoliceStation[]>([]);
    const [showMapPoi, setShowMapPoi] = useState<boolean>(false);
    const [showOccurrences, setShowOccurrences] = useState<boolean>(true);
    const [showAgents, setShowAgents] = useState<boolean>(true);
    const [showPoliceStations, setShowPoliceStations] = useState<boolean>(true);

    const [polygons, setPolygons] = useState<MapPolygon[]>([]);
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [statistics, setStatistics] = useState<Statistic>();

    const isFocused = useIsFocused();
    const isEnabled = !!authenticatedUser && isFocused;

    const resumeStatisticsQuery = useQuery({
        queryKey: ['resumeStatistics', actingBodyId],
        queryFn: () => dispatchService.getStatistics(actingBodyId ?? undefined),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    const resumeOccurrencePositionsQuery = useQuery({
        queryKey: ['resumeOccurrencePositions', actingBodyId],
        queryFn: () => dispatchService.getOccurrencePositions(),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    const resumeAgentPositionsQuery = useQuery({
        queryKey: ['resumeAgentPositions', actingBodyId],
        queryFn: () => dispatchService.getAgentPositions(undefined, actingBodyId ?? undefined),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    const resumePoliceStationsQuery = useQuery({
        queryKey: ['resumePoliceStations'],
        queryFn: () => dispatchService.getPoliceStationsPositions(),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    useEffect(() => {
        if (!resumeStatisticsQuery.data) {
            return;
        }
        setStatistics(resumeStatisticsQuery.data);
    }, [resumeStatisticsQuery.data]);

    useEffect(() => {
        if (!resumeOccurrencePositionsQuery.data) {
            return;
        }
        setOccurrencePositions(resumeOccurrencePositionsQuery.data);
    }, [resumeOccurrencePositionsQuery.data]);

    useEffect(() => {
        if (!resumeAgentPositionsQuery.data) {
            return;
        }
        setAgentPositions(resumeAgentPositionsQuery.data);
    }, [resumeAgentPositionsQuery.data]);

    useEffect(() => {
        if (!resumePoliceStationsQuery.data) {
            return;
        }
        setPoliceStationsPositions(resumePoliceStationsQuery.data);
    }, [resumePoliceStationsQuery.data]);

    return <Container>
        <Header
            title={translate('general_vision')}
        />
        <View style={{ flex: 1, rowGap: 15 }}>
            <View style={styles.header}>
                {!resumeStatisticsQuery.isInitialLoading && statistics ? <>
                    <View style={{
                        flexDirection: 'column',
                        flex: 1,
                    }}>
                        <MyAppText style={styles.statisticsHeader}>{translate('teamsResume')}</MyAppText>
                        <View style={styles.statisticsList}>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.allActiveTeams}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('actives')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.teams2Wheels}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('2WheelsTeams')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.teams4Wheels}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('4WheelsTeams')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.teamsOnFoot}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('OnFootTeams')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.otherTeams}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('otherTeams')}</MyAppText>
                            </View>
                        </View>
                    </View>
                    <View style={{
                        flexDirection: 'column',
                        flex: 1,
                    }}>
                        <MyAppText style={styles.statisticsHeader}>{translate('occurrences_resume')}</MyAppText>
                        <View style={styles.statisticsList}>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.pendingOccurrences}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('pending')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.inServiceOccurrences}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('in_service')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.onGoingOccurrences}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('ongoing')}</MyAppText>
                            </View>
                            <View style={styles.statistic}>
                                <MyAppText style={styles.statisticNumber}>{statistics.concludedOccurrences}</MyAppText>
                                <MyAppText style={styles.statisticLabel}>{translate('concluded')} ({translate('last12Hours')})</MyAppText>
                            </View>
                        </View>
                    </View>
                </> : <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size='large' color={theme.colors.loadingColor} /></View>}
            </View>
            <Card>
                <View style={styles.mapTopBar}>
                    <View style={styles.filters}>
                        <View>
                            <MyCheckbox
                                style={styles.checkbox}
                                label={translate('occurrences')}
                                checked={showOccurrences}
                                setChecked={setShowOccurrences}
                            />
                        </View>
                        <View>
                            <MyCheckbox
                                style={styles.checkbox}
                                label={translate('agents')}
                                checked={showAgents}
                                setChecked={setShowAgents}
                            />
                        </View>
                        <View>
                            <MyCheckbox
                                style={styles.checkbox}
                                label={translate('policeStation')}
                                checked={showPoliceStations}
                                setChecked={setShowPoliceStations}
                            />
                        </View>
                    </View>
                </View>
                <View style={styles.mapBottomFilters}>
                    <MapFilter
                        polygons={polygons}
                        setPolygons={setPolygons}
                        actingBodyId={actingBodyId}
                        setActingBodyId={setActingBodyId}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                    <View>
                        <View style={[styles.filters, windowInfo.isMobile && { opacity: isOpen ? 0 : 100 }]}>
                            <MyCheckbox
                                style={styles.checkbox}
                                label={translate('pointsOfInterest')}
                                checked={showMapPoi}
                                setChecked={setShowMapPoi}
                            />
                        </View>
                    </View>
                </View>
                <Map
                    showMapPoi={showMapPoi}
                    occurrencePositions={showOccurrences ? occurrencePositions : []}
                    agentPositions={showAgents ? agentPositions : []}
                    policeStations={showPoliceStations ? policeStationsPositions : []}
                    polygons={polygons}
                    isLoading={resumeOccurrencePositionsQuery.isInitialLoading && resumeAgentPositionsQuery.isInitialLoading && resumePoliceStationsQuery.isInitialLoading}
                />
            </Card>
        </View>
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    header: {
        flexDirection: 'column',
        minHeight: 150
    },
    statistic: {
        flex: 1,
    },
    statisticsHeader: {
        color: theme.colors.primaryButton.background,
        fontSize: 16
    },
    statisticsList: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
    },
    statisticNumber: {
        color: theme.colors.primaryButton.background,
        fontSize: 28
    },
    statisticLabel: {
        color: theme.colors.card.detailText,
        fontSize: 12,
    },
    mapTopBar: {
        position: 'absolute',
        zIndex: 1000,
        top: 10,
        left: 10,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    mapBottomFilters: {
        position: 'absolute',
        display: 'flex',
        flexDirection: 'row',
        zIndex: 1000,
        bottom: 18,
        left: 20,
        gap: 8,
        alignItems: 'flex-end'
    },
    checkbox: {
        height: 38
    },
    filters: {
        flexDirection: 'column',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.placeholder,
        maxWidth: 435,
        paddingHorizontal: 8,
        gap: 8,
        paddingBottom: 8,
        paddingTop: 8,
    },
}));
